// Gradient Builder - Modal Provider
// React Context for managing gradient builder modal state

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { GradientBuilderModal } from "../ui/common_components/GradientBuilderModal";
import { usePromptInputActions, usePrompt } from "../view_stores/usePromptInputStore";

interface GradientBuilderModalContextType {
  open: () => void;
  close: () => void;
}

const GradientBuilderModalContext = createContext<
  GradientBuilderModalContextType | undefined
>(undefined);

export function useGradientBuilderModal() {
  const ctx = useContext(GradientBuilderModalContext);
  if (!ctx)
    throw new Error(
      "useGradientBuilderModal must be used within a GradientBuilderModalProvider"
    );
  return ctx;
}

export function GradientBuilderModalProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const prompt = usePrompt();
  const { setPrompt } = usePromptInputActions();

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleInsertToPrompt = useCallback((code: string) => {
    // Append code to current prompt (or set as new prompt if empty)
    const currentPrompt = prompt ?? "";
    const newPrompt = currentPrompt
      ? `${currentPrompt}\n\n${code}`
      : code;
    setPrompt(newPrompt);
    setIsOpen(false);
  }, [prompt, setPrompt]);

  return (
    <GradientBuilderModalContext.Provider value={{ open, close }}>
      {children}
      {isOpen && (
        <GradientBuilderModal
          onClose={close}
          onInsertToPrompt={handleInsertToPrompt}
        />
      )}
    </GradientBuilderModalContext.Provider>
  );
}
